import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * D-323 s3: dictation (speech -> text) and a voice note (audio file).
 * Dictation uses the browser SpeechRecognition (Hebrew). While dictating the microphone is also recorded;
 * if the dictation produced no text (NetFree / the service is blocked) the recording is KEPT and attached
 * as the voice note, otherwise it is dropped.
 */

type Rec = {
  lang: string; continuous: boolean; interimResults: boolean;
  onresult: ((e: any) => void) | null; onerror: ((e: any) => void) | null; onend: (() => void) | null;
  start(): void; stop(): void; abort(): void;
};

function recognitionCtor(): (new () => Rec) | null {
  const w = window as any;
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

export type VoiceError = 'unavailable' | 'denied' | null;

export function useVoiceCapture(onText: (text: string) => void) {
  const [dictating, setDictating] = useState(false);
  const [recording, setRecording] = useState(false);
  const [audio, setAudio] = useState<Blob | null>(null);
  const [error, setError] = useState<VoiceError>(null);

  const recRef = useRef<Rec | null>(null);
  const mediaRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const gotTextRef = useRef(false);
  const keepAudioRef = useRef(false);
  const onTextRef = useRef(onText);
  onTextRef.current = onText;

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((tr) => tr.stop());
    streamRef.current = null;
  };

  const startMedia = useCallback(async (keep: boolean): Promise<boolean> => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') { setError('unavailable'); return false; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      keepAudioRef.current = keep;
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        stopStream();
        if (keepAudioRef.current && chunksRef.current.length > 0) {
          setAudio(new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' }));
        }
        setRecording(false);
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
      return true;
    } catch {
      setError('denied');
      return false;
    }
  }, []);

  const stopMedia = useCallback(() => {
    if (mediaRef.current && mediaRef.current.state !== 'inactive') mediaRef.current.stop();
    else { stopStream(); setRecording(false); }
  }, []);

  const startRecording = useCallback(async () => {
    setError(null);
    await startMedia(true);
  }, [startMedia]);

  const stopRecording = useCallback(() => stopMedia(), [stopMedia]);

  const startDictation = useCallback(async () => {
    setError(null);
    const Ctor = recognitionCtor();
    if (!Ctor) { setError('unavailable'); return; }
    gotTextRef.current = false;
    // The microphone is recorded too; it is kept only if the dictation turns out to be blocked.
    await startMedia(false);
    const rec = new Ctor();
    rec.lang = 'he-IL';
    rec.continuous = true;
    rec.interimResults = false;
    rec.onresult = (e: any) => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) {
          const text = String(e.results[i][0]?.transcript || '').trim();
          if (text) { gotTextRef.current = true; onTextRef.current(text); }
        }
      }
    };
    rec.onerror = (e: any) => {
      // network / service-not-allowed = blocked: keep the recorded audio as the report.
      if (!gotTextRef.current) keepAudioRef.current = true;
      setError(e?.error === 'not-allowed' ? 'denied' : 'unavailable');
    };
    rec.onend = () => {
      setDictating(false);
      if (!gotTextRef.current) keepAudioRef.current = true;
      stopMedia();
    };
    recRef.current = rec;
    try { rec.start(); setDictating(true); } catch { setError('unavailable'); keepAudioRef.current = true; stopMedia(); }
  }, [startMedia, stopMedia]);

  const stopDictation = useCallback(() => { recRef.current?.stop(); }, []);

  const clearAudio = useCallback(() => setAudio(null), []);

  useEffect(() => () => {
    try { recRef.current?.abort(); } catch { /* already stopped */ }
    keepAudioRef.current = false;
    if (mediaRef.current && mediaRef.current.state !== 'inactive') mediaRef.current.stop();
    stopStream();
  }, []);

  return {
    dictating, recording, audio, error,
    dictationSupported: recognitionCtor() !== null,
    startDictation, stopDictation, startRecording, stopRecording, clearAudio,
  };
}
